import {Controls, ControlsItem, Typography} from '@genestack/ui';
import React from 'react';

import {LocalBackdrop} from '../../../../components/local-backdrop';
import {LocalSpinner} from '../../../../components/local-spinner';
import {useDocRevisionsContext} from '../../../../providers/version';
import {useAddToRecentDocsMutation} from '../hooks/use-add-to-recent-docs-mutation';
import {useCreateDocumentMutation} from '../hooks/use-create-document-mutation';
import {useDocumentQuery} from '../hooks/use-document-query';

import {EditorBackdrop} from './editor-backdrop';
import {EditorLayout} from './editor-layout/editor-layout';
import {useHandleFindUsages} from '../../common/sidebar-search-field/hooks/use-handle-find-usages';
import {FindUsagesEditorEvent} from '../../../../../webeditor/biokb-editor-adapters';
import {EditorWrapper} from './editor-layout/editor-wrapper';
import {EditorHeaderSkeleton} from '../../../../components/editor/editor-header/editor-header-skeleton';
import {useDocumentSidebarState} from '../../common/right-sidebar/hooks/use-document-sidebar-state';

interface Props {
    accession: string;
    findUsagesProps: ReturnType<typeof useHandleFindUsages>;
    rightSidebarProps: ReturnType<typeof useDocumentSidebarState>;
    revisionId?: string;
}

export function Editor(props: Props) {
    const documentQuery = useDocumentQuery(props.accession);
    const createDocMutation = useCreateDocumentMutation();
    const versionContext = useDocRevisionsContext();
    const addToRecentMutation = useAddToRecentDocsMutation();

    const [isEditorLoading, setEditorLoadingStatus] = React.useState(false);

    const findUsages = React.useCallback(
        (event: FindUsagesEditorEvent) =>
            props.findUsagesProps.findUsages(event, props.accession, props.revisionId),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [props.findUsagesProps.findUsages, props.revisionId, props.accession]
    );

    const isVersionNotFound = Boolean(
        props.revisionId && versionContext.revisions && !versionContext.selectedRevision
    );

    React.useEffect(() => {
        if (!documentQuery.data?.accession) {
            return;
        }

        // adding doc to recently viewed bc currently it is performed on FE
        addToRecentMutation.mutate(documentQuery.data);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documentQuery.data?.accession]);

    if (!documentQuery.data || isVersionNotFound) {
        return (
            <EditorWrapper>
                <EditorHeaderSkeleton />
                <EditorBackdrop
                    accession={props.accession}
                    isDocumentLoading={documentQuery.isLoading || isEditorLoading}
                    isVersionNotFound={isVersionNotFound}
                    isDocumentNotFound={!documentQuery.isLoading && !documentQuery.data}
                />
            </EditorWrapper>
        );
    }

    if (createDocMutation.isPending) {
        return (
            <EditorWrapper>
                <LocalBackdrop open variant="accent">
                    <Controls justify="center">
                        <ControlsItem>
                            <LocalSpinner />
                        </ControlsItem>
                        <ControlsItem>
                            <Typography variant="section" inverted>
                                Creating new document…
                            </Typography>
                        </ControlsItem>
                    </Controls>
                </LocalBackdrop>
            </EditorWrapper>
        );
    }

    return (
        <EditorLayout
            accession={props.accession}
            onLoadingStateChange={setEditorLoadingStatus}
            document={documentQuery.data}
            findUsages={findUsages}
            rightSidebarProps={props.rightSidebarProps}
            revisionId={props.revisionId}
        />
    );
}
